/**
 * Web origins of an embed's allowlist (D426, ADR-0067).
 *
 * An allowed origin is EXACTLY `scheme://host[:port]` — the value a browser sends in the `Origin` header. No path, no
 * query, no fragment, no credentials and no wildcard: a partner lists every site it embeds on. `https` is required,
 * except for a loopback host (`localhost`, `127.0.0.1`, `[::1]`) where plain `http` is allowed for development. A host is
 * ASCII (an internationalised name is written in its punycode `xn--` form, as the browser sends it). The origin is
 * normalised (lowercase, the scheme's default port dropped, one trailing slash tolerated on input) so matching is a plain
 * string comparison of two normalised values. The literal `null` origin (sandboxed frames, `file:`) never matches.
 */
import { err, ok } from '@vello/core';
import { refuse } from './reasons.js';
/** The most origins one embed may list. */
export const WIDGET_MAX_ORIGINS = 20;
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);
const DEFAULT_PORT = { http: '80', https: '443' };
const ORIGIN_SHAPE = /^([a-z][a-z0-9+.-]*):\/\/([^/?#@]+?)(?::(\d{1,5}))?\/?$/;
const DNS_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const IPV6_LITERAL = /^\[[0-9a-f:.]+\]$/;
function validHost(host) {
    if (IPV6_LITERAL.test(host))
        return true;
    if (host.length > 253)
        return false;
    const labels = host.split('.');
    return labels.every((l) => DNS_LABEL.test(l));
}
/**
 * Normalise one origin, or refuse it with a stable code: `WIDGET_ORIGIN_WILDCARD` (a `*` anywhere),
 * `WIDGET_ORIGIN_INSECURE` (plain http off loopback), `WIDGET_ORIGIN_MALFORMED` (anything that is not exactly an origin).
 */
export function normalizeOrigin(raw) {
    if (typeof raw !== 'string')
        return err(refuse('WIDGET_ORIGIN_MALFORMED', 'origin must be a string'));
    const input = raw.trim().toLowerCase();
    if (input.includes('*'))
        return err(refuse('WIDGET_ORIGIN_WILDCARD', 'wildcard origins are not allowed'));
    if (input === '' || input === 'null' || input.length > 300)
        return err(refuse('WIDGET_ORIGIN_MALFORMED', 'not an origin'));
    const m = ORIGIN_SHAPE.exec(input);
    if (m === null)
        return err(refuse('WIDGET_ORIGIN_MALFORMED', 'expected scheme://host[:port]'));
    const scheme = m[1];
    const host = m[2];
    const port = m[3];
    if (scheme !== 'https' && scheme !== 'http')
        return err(refuse('WIDGET_ORIGIN_MALFORMED', 'scheme must be https'));
    if (!validHost(host))
        return err(refuse('WIDGET_ORIGIN_MALFORMED', 'invalid host'));
    if (scheme === 'http' && !LOOPBACK_HOSTS.has(host))
        return err(refuse('WIDGET_ORIGIN_INSECURE', 'http is allowed only on a loopback host'));
    if (port !== undefined) {
        const n = Number(port);
        if (!Number.isInteger(n) || n < 1 || n > 65535 || String(n) !== port)
            return err(refuse('WIDGET_ORIGIN_MALFORMED', 'invalid port'));
    }
    const keepPort = port !== undefined && port !== DEFAULT_PORT[scheme];
    return ok(`${scheme}://${host}${keepPort ? `:${port}` : ''}`);
}
/**
 * Validate an allowlist: 1..20 origins, each normalised; duplicates (after normalisation) collapse; order is kept.
 * The first bad entry refuses the whole list.
 */
export function normalizeOriginList(raw) {
    if (!Array.isArray(raw))
        return err(refuse('WIDGET_ORIGINS_EMPTY', 'allowedOrigins must be a non-empty array'));
    if (raw.length === 0)
        return err(refuse('WIDGET_ORIGINS_EMPTY', 'allowedOrigins must be a non-empty array'));
    const out = [];
    for (const entry of raw) {
        const n = normalizeOrigin(entry);
        if (!n.ok)
            return n;
        if (!out.includes(n.value))
            out.push(n.value);
    }
    if (out.length > WIDGET_MAX_ORIGINS)
        return err(refuse('WIDGET_TOO_MANY_ORIGINS', `at most ${WIDGET_MAX_ORIGINS} origins`));
    return ok(out);
}
/**
 * Whether a request's `Origin` header is on an allowlist. Absent, `null`, malformed or not listed → false. The header is
 * normalised with the same rule as the list, so `https://Site.example:443` matches `https://site.example`.
 */
export function originAllowed(allowed, requestOrigin) {
    if (requestOrigin === undefined)
        return false;
    const n = normalizeOrigin(requestOrigin);
    return n.ok && allowed.includes(n.value);
}
//# sourceMappingURL=origin.js.map